// Where a one-line, horizontally scrolling step rail OPENS (EngagementSurface).
//
// The rail opens with the step you are on in view (EngagementSurface's own
// header on why). The first cut computed that as "just enough to bring the
// current step's right edge in", which lands anywhere — and on a 390px phone
// it landed a few pixels into the first step, so the rail opened reading
// "FFERED" (the CallToWrap demo walk, 2026-09-27). It also measured with
// `offsetLeft`, which is relative to the offsetParent, not the rail — the
// page's own padding was counted as overshoot.
//
// The rule now: never a clipped word at the left edge.
//   - If the current step fits with the rail at its start, the rail stays at
//     its start (0) and the first step is whole.
//   - Otherwise it opens exactly on a step boundary: the first step whose
//     left edge is at or past the overshoot, so the leftmost visible step is
//     whole and the current step is in view.
// Positions are the rail's SCROLL coordinates: a cell's left edge from the rail's own left edge at
// scrollLeft 0 (getBoundingClientRect − the rail's + its scrollLeft), so no offsetParent is involved.
//
// ⚑ The third cut (the 390px walk, 2026-09-29). A late-stage booking's boundary can lie PAST the
// rail's maximum scroll: the last step always ends flush with the content, so for WRAPPED the
// boundary the rule wants (CONTRACTED, 188px) exceeded scrollWidth − clientWidth (153px), the
// browser clamped the assignment to 153, and the rail opened reading "ED". The fix is an end
// spacer exactly as wide as the shortfall, so the boundary is reachable: `openingPosition`
// returns both the scroll and that spacer width. (A comment in the second cut blamed "~35px
// of rail padding"; the rail has none — the 35px was the clamp.)

export interface RailCell {
  left: number;
  width: number;
}

/** Slack kept to the right of the current step, in px. */
export const RAIL_RIGHT_SLACK = 12;

export function openingScrollLeft(cells: RailCell[], currentIndex: number, railWidth: number): number {
  const cur = cells[currentIndex];
  if (!cur) return 0;
  const overshoot = cur.left + cur.width + RAIL_RIGHT_SLACK - railWidth;
  if (overshoot <= 0) return 0;
  for (let i = 1; i <= currentIndex; i++) {
    const left = cells[i]!.left;
    if (left >= overshoot) return left;
  }
  return cur.left;
}

/** Where the rail opens AND how much blank space must follow the last step so that position is
 * reachable (a scroll past `scrollWidth − clientWidth` is clamped by the browser). */
export function openingPosition(
  cells: RailCell[],
  currentIndex: number,
  railWidth: number
): { scrollLeft: number; endPad: number } {
  const scrollLeft = openingScrollLeft(cells, currentIndex, railWidth);
  const last = cells[cells.length - 1];
  const contentRight = last ? last.left + last.width : 0;
  return { scrollLeft, endPad: Math.max(0, Math.ceil(scrollLeft + railWidth - contentRight)) };
}
